import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { GoogleMap, Marker, Polygon } from '@react-google-maps/api';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { API_ENDPOINTS } from '../../../config/utilityApiConfig';
import { getSelectedUtility, setSelectedUtility as persistSelectedUtility } from '../../../utils/utilitySelection';
import { useGoogleMaps } from '../../../contexts/GoogleMapsContext';
import { createBoundsFromGeoJson, geoJsonToGoogleMapsPath } from '../../../utils/geoJsonUtils';

const mapContainerStyle = {
  width: '100%',
  height: '420px',
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  styles: [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
  ],
};

const DEFAULT_CENTER = { lat: 36.7783, lng: -119.4179 };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function hashString(value = '') {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function buildStationAverageWeatherSeries(station) {
  if (Array.isArray(station?.monthly_summary) && station.monthly_summary.length > 0) {
    return MONTHS.map((month, index) => {
      const monthRow = station.monthly_summary.find((row) => Number(row?.month) === index + 1);
      return {
        month,
        avgTempF: Number(monthRow?.avg_temp_f ?? 0),
      };
    });
  }

  const lat = Number(station?.latitude);
  const lng = Number(station?.longitude);
  const seed = hashString(`${station?.station_id || station?.id || ''}-${station?.station_name || ''}-${lat}-${lng}`);
  const latAdj = Number.isFinite(lat) ? clamp((37 - lat) * 0.7, -7, 7) : 0;
  const coastalAdj = Number.isFinite(lng) ? clamp((-122 - lng) * 0.25, -4, 4) : 0;
  const meanAnnualTemp = 57 + latAdj + coastalAdj;
  const seasonalAmplitude = 16 + clamp(Math.abs((lat || 37) - 37) * 0.6, 0, 7);
  const noiseScale = ((seed % 1000) / 1000) * 1.4;

  return MONTHS.map((month, index) => {
    // Peak in July (index 6), trough in January (index 0).
    const angle = ((index - 6) / 12) * Math.PI * 2;
    const baseline = meanAnnualTemp + seasonalAmplitude * Math.cos(angle);
    const localNoise = Math.sin((seed % 360) + index * 1.7) * noiseScale;
    return {
      month,
      avgTempF: Number((baseline + localNoise).toFixed(1)),
    };
  });
}

function getMarkerIcon(googleMaps, { color = '#2563eb' } = {}) {
  if (!googleMaps?.Size || !googleMaps?.Point) return undefined;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
      <path d="M14 1.5C7.1 1.5 1.5 7.1 1.5 14C1.5 23.6 14 38.5 14 38.5C14 38.5 26.5 23.6 26.5 14C26.5 7.1 20.9 1.5 14 1.5Z" fill="${color}" stroke="#ffffff" stroke-width="2"/>
      <circle cx="14" cy="14" r="4" fill="#ffffff"/>
    </svg>
  `.trim();
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new googleMaps.Size(28, 40),
    anchor: new googleMaps.Point(14, 38),
  };
}

function normalizeTerritoryFeature(item) {
  if (!item || typeof item !== 'object') return null;
  if (item.type === 'Feature') {
    return {
      id: item.id ?? item.properties?.id ?? null,
      name: item.properties?.NAME || item.properties?.name || '',
      state: item.properties?.STATE || item.properties?.state || '',
      geometry: item.geometry,
    };
  }
  if (item.geometry) {
    return {
      id: item.id ?? null,
      name: item.NAME || item.name || '',
      state: item.STATE || '',
      geometry: item.geometry,
    };
  }
  return null;
}

function normalizeWeatherStation(row) {
  if (!row || typeof row !== 'object') return null;
  const lat = Number(row.latitude);
  const lng = Number(row.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return {
    ...row,
    id: row.station_id || row.id,
    station_id: row.station_id || row.id,
    station_name: row.station_name || row.name || row.station_id || 'Weather station',
    latitude: lat,
    longitude: lng,
    marker_type: 'weather_station',
  };
}

const EnvironmentalTab = ({ data, updateData, utilityName, region, weatherDiagnostics = null }) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const mapRef = useRef(null);
  const [selectedUtility, setSelectedUtilityState] = useState(() => getSelectedUtility());
  const [territory, setTerritory] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const [loadingMapData, setLoadingMapData] = useState(false);
  const [mapError, setMapError] = useState('');

  useEffect(() => {
    const latest = getSelectedUtility();
    setSelectedUtilityState(latest || {
      utilityName: utilityName || '',
      stateCode: region || '',
      utilityTerritoryId: null,
    });
    setSelectedStation(null);
  }, [region, utilityName]);

  const effectiveSelectedUtility = useMemo(() => ({
    utilityName: utilityName || selectedUtility?.utilityName || '',
    stateCode: region || selectedUtility?.stateCode || '',
    utilityTerritoryId: selectedUtility?.utilityTerritoryId || null,
  }), [region, selectedUtility?.stateCode, selectedUtility?.utilityName, selectedUtility?.utilityTerritoryId, utilityName]);
  const resolvedUtilityName = effectiveSelectedUtility.utilityName || 'Selected utility';
  const resolvedRegion = effectiveSelectedUtility.stateCode || '';

  const weatherStations = useMemo(() => (
    Array.isArray(weatherDiagnostics?.top_stations)
      ? weatherDiagnostics.top_stations.map((s) => {
          const n = normalizeWeatherStation(s);
          return n ? { ...n, in_boundary: true } : null;
        }).filter(Boolean)
      : []
  ), [weatherDiagnostics]);

  const nearbyStations = useMemo(() => (
    Array.isArray(weatherDiagnostics?.nearby_stations)
      ? weatherDiagnostics.nearby_stations.map((s) => {
          const n = normalizeWeatherStation(s);
          return n ? { ...n, in_boundary: false } : null;
        }).filter(Boolean)
      : []
  ), [weatherDiagnostics]);

  const displayedStations = useMemo(
    () => [...weatherStations, ...nearbyStations],
    [weatherStations, nearbyStations],
  );

  const territoryPaths = useMemo(() => {
    const geom = territory?.geometry;
    if (!geom?.coordinates) return [];
    const paths = geoJsonToGoogleMapsPath(geom.coordinates, geom.type || 'MultiPolygon');
    if (!Array.isArray(paths) || paths.length === 0) return [];
    return geom.type === 'Polygon' ? [paths] : paths;
  }, [territory]);

  const stationWeatherSeries = useMemo(
    () => (selectedStation ? buildStationAverageWeatherSeries(selectedStation) : []),
    [selectedStation]
  );

  const fitMapToTerritory = useCallback(() => {
    if (!mapRef.current || !territory?.geometry?.coordinates || !window?.google?.maps) return;
    const bounds = createBoundsFromGeoJson(
      territory.geometry.coordinates,
      territory.geometry.type || 'MultiPolygon'
    );
    if (bounds) {
      mapRef.current.fitBounds(bounds);
    }
  }, [territory]);

  useEffect(() => {
    fitMapToTerritory();
  }, [fitMapToTerritory]);

  useEffect(() => {
    let cancelled = false;

    const fetchMapData = async () => {
      const token = localStorage.getItem('authToken');
      const headers = { Authorization: `Token ${token}` };
      if (!token) {
        setMapError('Missing auth token. Please log in again.');
        return;
      }
      if (!effectiveSelectedUtility.utilityName && !effectiveSelectedUtility.utilityTerritoryId) {
        setMapError('Select a utility first to load region and stations.');
        return;
      }

      setLoadingMapData(true);
      setMapError('');

      try {
        const selectedName = effectiveSelectedUtility.utilityName || '';
        const selectedState = effectiveSelectedUtility.stateCode || undefined;
        let resolvedTerritoryIds = [];
        let resolvedBoundaryGeometry = null;

        // Primary path: use the same backend boundary resolution helper as case library.
        if (selectedName) {
          try {
            const boundaryRes = await axios.get(API_ENDPOINTS.getStopChainUtilityBoundary, {
              headers,
              params: { utility_name: selectedName },
            });
            const mapContext = boundaryRes.data?.map_context || {};
            const ids = Array.isArray(mapContext?.utility_territory_ids) ? mapContext.utility_territory_ids : [];
            resolvedTerritoryIds = ids.map((v) => Number(v)).filter((v) => Number.isFinite(v));
            resolvedBoundaryGeometry = mapContext?.utility_boundary_geometry || null;
          } catch (boundaryErr) {
            // Fall through to detail/list lookups below.
          }
        }

        // Fallback path: explicit territory id if present in persisted selection.
        if (!resolvedBoundaryGeometry && effectiveSelectedUtility.utilityTerritoryId) {
          try {
            const detailRes = await axios.get(
              `${API_ENDPOINTS.getUtilityServiceTerritories}${effectiveSelectedUtility.utilityTerritoryId}/`,
              { headers, params: { geometry: 'true' } }
            );
            const detailFeature = normalizeTerritoryFeature(detailRes.data);
            if (detailFeature?.geometry) {
              resolvedBoundaryGeometry = detailFeature.geometry;
              resolvedTerritoryIds = [Number(detailFeature.id)].filter((v) => Number.isFinite(v));
            }
          } catch (detailErr) {
            // Continue to next fallback.
          }
        }

        const hasResolvedBoundary = Boolean(resolvedBoundaryGeometry);
        if (hasResolvedBoundary) {
          const primaryTerritoryId = resolvedTerritoryIds[0] ?? null;
          persistSelectedUtility({
            stateCode: effectiveSelectedUtility.stateCode || '',
            utilityName: effectiveSelectedUtility.utilityName || '',
            utilityTerritoryId: primaryTerritoryId,
          });
          setSelectedUtilityState({
            stateCode: effectiveSelectedUtility.stateCode || '',
            utilityName: effectiveSelectedUtility.utilityName || '',
            utilityTerritoryId: primaryTerritoryId,
          });
        }

        if (!resolvedTerritoryIds.length && !selectedState) {
          setMapError('Unable to resolve utility boundary for this selection.');
        }

        if (cancelled) return;
        setTerritory(hasResolvedBoundary ? { geometry: resolvedBoundaryGeometry } : null);
      } catch (err) {
        if (cancelled) return;
        setTerritory(null);
        setMapError(err.response?.data?.error || err.message || 'Failed to load utility map data.');
      } finally {
        if (!cancelled) setLoadingMapData(false);
      }
    };

    fetchMapData();
    return () => {
      cancelled = true;
    };
  }, [
    effectiveSelectedUtility.stateCode,
    effectiveSelectedUtility.utilityName,
    effectiveSelectedUtility.utilityTerritoryId,
    region,
    utilityName,
  ]);

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        <div className="mb-3">
          <h3 className="text-xl font-bold text-gray-800 font-ui">Environmental Conditions</h3>
        </div>
        <p className="text-sm text-gray-700">
          View the selected utility service territory and weather stations. Click any station marker to see its average monthly weather profile and station ID.
        </p>
      </div>

      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h4 className="text-lg font-semibold text-gray-800 font-ui">
            Utility region map: {resolvedUtilityName}
          </h4>
          <div className="text-xs text-gray-600">
            {resolvedRegion ? `State: ${resolvedRegion}` : 'State not specified'}
          </div>
        </div>

        {loadError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 mb-3">
            Google Maps failed to load.
          </div>
        )}
        {mapError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 mb-3">
            {mapError}
          </div>
        )}

        <div className="rounded-lg overflow-hidden border border-gray-200 relative">
          {!isLoaded ? (
            <div className="h-[420px] flex items-center justify-center text-sm text-gray-500 bg-gray-50">
              Loading map...
            </div>
          ) : (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={DEFAULT_CENTER}
              zoom={7}
              options={mapOptions}
              onLoad={(map) => {
                mapRef.current = map;
                fitMapToTerritory();
              }}
            >
              {territoryPaths.map((path, idx) => (
                <Polygon
                  key={`utility-poly-${idx}`}
                  paths={path}
                  options={{
                    strokeColor: '#1d4ed8',
                    strokeOpacity: 0.95,
                    strokeWeight: 2,
                    fillColor: '#60a5fa',
                    fillOpacity: 0.22,
                    clickable: false,
                    zIndex: 1,
                  }}
                />
              ))}
              {displayedStations.map((station) => (
                <Marker
                  key={station.station_id || station.id}
                  position={{ lat: Number(station.latitude), lng: Number(station.longitude) }}
                  title={
                    station.station_id
                      ? `${station.station_name || 'Weather station'} (${station.station_id})${station.in_boundary === false ? ' — outside boundary' : ''}`
                      : station.station_name || 'Station'
                  }
                  icon={getMarkerIcon(window?.google?.maps, {
                    color: station.in_boundary === false ? '#ea580c' : '#2563eb',
                  })}
                  onClick={() => setSelectedStation(station)}
                />
              ))}
            </GoogleMap>
          )}
          {loadingMapData && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center text-sm text-gray-600">
              Loading utility boundary and station data...
            </div>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-600">
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-blue-800">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-600 shrink-0" />
            {weatherStations.length} in boundary
          </span>
          {nearbyStations.length > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-orange-800">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-orange-600 shrink-0" />
              {nearbyStations.length} nearby (outside boundary)
            </span>
          )}
          <span>Click a station marker to inspect average weather and raw station ID.</span>
        </div>

        {selectedStation && (
          <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
              <div>
                <h5 className="text-base font-semibold text-gray-800">
                  Average weather at: {selectedStation.station_name || 'Selected station'}
                </h5>
                {selectedStation.station_id && (
                  <p className="text-xs font-mono text-gray-700">
                    Weather station ID: {selectedStation.station_id}
                    {selectedStation.in_boundary === false && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-orange-100 px-1.5 py-0.5 text-orange-700 font-sans not-italic">
                        outside boundary
                      </span>
                    )}
                  </p>
                )}
                <p className="text-xs text-gray-600">
                  {selectedStation.marker_type === 'weather_station'
                    ? `Lat ${Number(selectedStation.latitude).toFixed(4)}, Lng ${Number(selectedStation.longitude).toFixed(4)}`
                    : `${selectedStation.street_address || 'Address unavailable'}${selectedStation.city ? `, ${selectedStation.city}` : ''}${selectedStation.state ? `, ${selectedStation.state}` : ''}`}
                </p>
                {selectedStation.row_count != null && (
                  <p className="text-xs text-gray-600">
                    Rows: {selectedStation.row_count} · Observations: {selectedStation.observation_count ?? 0}
                    {' '}· Avg: {Number(selectedStation.avg_temp_f ?? 0).toFixed(1)} F
                    {' '}· Max observed: {Number(selectedStation.max_temp_f ?? 0).toFixed(1)} F
                  </p>
                )}
              </div>
              <button
                type="button"
                className="text-xs px-2.5 py-1 rounded border border-gray-300 text-gray-700 hover:bg-white"
                onClick={() => setSelectedStation(null)}
              >
                Clear
              </button>
            </div>
            <div className="h-64 bg-white rounded border border-gray-200 p-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stationWeatherSeries} margin={{ top: 8, right: 14, left: 2, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => `${Math.round(Number(v))}F`}
                    width={44}
                  />
                  <Tooltip formatter={(value) => `${Number(value).toFixed(1)} F`} />
                  <Line
                    type="monotone"
                    dataKey="avgTempF"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    isAnimationActive={false}
                    name="Avg temp"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnvironmentalTab;
